<script setup lang="ts">
import { ref } from 'vue'
import { useRoute } from 'vue-router'

import { ChevronDown } from 'lucide-vue-next'
import type { Component } from 'vue'
import { useDark } from '@vueuse/core'

interface Props {
  icon: Component
  isExpanded: boolean
  label: string
  route?: string
  isActive?: boolean
  children?: { title: string; path: string; icon?: Component }[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  click: []
  openFloatingMenu: [
    event: MouseEvent,
    children: { title: string; path: string; icon?: Component }[],
  ]
}>()
const isDark = useDark({ selector: 'html' })
const currentRoute = useRoute()
const isSubmenuOpen = ref(false)

const handleClick = (event?: MouseEvent) => {
  if (props.children) {
    if (props.isExpanded) {
      isSubmenuOpen.value = !isSubmenuOpen.value
    } else if (event) {
      emit('openFloatingMenu', event, props.children)
    }
  } else {
    emit('click')
  }
}

const isRouteActive = () => {
  const currentPath = currentRoute.path

  // Si el item tiene hijos, verificar si algún hijo está activo o si la ruta actual comienza con el path del hijo
  if (props.children) {
    return props.children.some(
      (child) => currentPath === child.path || currentPath.startsWith(child.path + '/'),
    )
  }

  // Si no tiene hijos, comparar directamente o verificar si es una ruta anidada
  if (props.route) {
    return currentPath === props.route || currentPath.startsWith(props.route + '/')
  }

  return false
}

const isChildActive = (childPath: string) => {
  const currentPath = currentRoute.path
  return currentPath === childPath || currentPath.startsWith(childPath + '/')
}

const sidebarOptionClasses = (active: boolean) => {
  if (active) {
    return ['sidebar-option-active', isDark.value ? 'sidebar-option-active-dark' : '']
  }

  return ['sidebar-option-idle', isDark.value ? 'sidebar-option-idle-dark' : '']
}
</script>

<template>
  <li class="w-full">
    <!-- Router link para navegación sin hijos -->
    <router-link
      v-if="route && !children"
      :to="route"
      class="sidebar-option group flex items-center gap-3 mb-1 p-2.5 rounded-lg border border-transparent transition-all duration-200 ease-in-out"
      :class="[
        { 'justify-center': !isExpanded },
        sidebarOptionClasses(isRouteActive()),
      ]"
    >
      <component
        :is="icon"
        :size="22"
        class="shrink-0 transition-colors duration-200"
        :class="
          isRouteActive()
            ? 'text-white dark:text-white'
            : 'text-[#565b9a] group-hover:text-[#7c4ff3] dark:text-white/75 dark:group-hover:text-white'
        "
      />
      <span
        v-if="isExpanded"
        class="text-sm font-semibold quicksand transition-all duration-200 truncate"
        :class="
          isRouteActive()
            ? 'text-white dark:text-white'
            : 'text-[#565b9a] group-hover:text-[#7c4ff3] dark:text-white/75 dark:group-hover:text-white'
        "
      >
        {{ label }}
      </span>
    </router-link>

    <hr
      v-if="route && !children"
      class="mx-auto border-0 border-b border-b-[#c9b8fb] w-8/9 opacity-80 dark:border-b-white/10"
    />

    <!-- Elemento con hijos (submenu) -->
    <div v-else-if="children" class="w-full">
      <button
        @click="handleClick($event)"
        class="sidebar-option group flex items-center gap-3 w-full p-2.5 rounded-lg border border-transparent transition-all duration-200 ease-in-out"
        :class="[
          { 'justify-center': !isExpanded },
          sidebarOptionClasses(isRouteActive()),
        ]"
      >
        <component
          :is="icon"
          :size="20"
          :stroke-width="2"
          class="shrink-0 transition-colors duration-200"
          :class="
            isRouteActive()
              ? 'text-white dark:text-white'
              : 'text-[#565b9a] group-hover:text-[#7c4ff3] dark:text-white/75 dark:group-hover:text-white'
          "
        />
        <span
          v-if="isExpanded"
          class="text-sm font-medium flex-1 text-left transition-all duration-200 truncate"
          :class="
            isRouteActive()
              ? 'text-white dark:text-white'
              : 'text-[#565b9a] group-hover:text-[#7c4ff3] dark:text-white/75 dark:group-hover:text-white'
          "
        >
          {{ label }}
        </span>
        <ChevronDown
          v-if="isExpanded"
          :size="16"
          class="transition-all duration-200"
          :class="[
            { 'rotate-180': isSubmenuOpen },
            isRouteActive() ? 'text-white dark:text-white' : 'text-[#565b9a] dark:text-white/75',
          ]"
        />
      </button>

      <!-- Submenu expandido (solo cuando el sidebar está expandido) -->
      <div v-if="isExpanded" class="overflow-hidden">
        <transition
          enter-active-class="transition-all duration-200 ease-out"
          leave-active-class="transition-all duration-150 ease-in"
          enter-from-class="opacity-0 max-h-0"
          enter-to-class="opacity-100 max-h-96"
          leave-from-class="opacity-100 max-h-96"
          leave-to-class="opacity-0 max-h-0"
        >
          <ul v-show="isSubmenuOpen" class="mt-1 ml-8 space-y-1">
            <li v-for="child in children" :key="child.path">
              <router-link
                :to="child.path"
                class="sidebar-option group flex items-center gap-2 p-2 pl-3 rounded-md border border-transparent transition-all duration-200"
                :class="
                  isChildActive(child.path)
                    ? [...sidebarOptionClasses(true), 'text-white dark:text-white']
                    : 'sidebar-option-idle text-[#565b9a] hover:text-[#7c4ff3] dark:text-white/75 dark:hover:text-white'
                "
              >
                <component
                  :is="child.icon"
                  v-if="child.icon"
                  :size="14"
                  :stroke-width="2"
                  class="shrink-0 transition-colors duration-200"
                />
                <div
                  v-else
                  class="w-1.5 h-1.5 rounded-full transition-colors duration-200"
                  :class="
                    isChildActive(child.path)
                      ? 'bg-white'
                      : 'bg-[#565b9a] group-hover:bg-[#7c4ff3] dark:bg-white/75 dark:group-hover:bg-white'
                  "
                ></div>
                <span class="text-xs font-medium truncate">{{ child.title }}</span>
              </router-link>
            </li>
          </ul>
        </transition>
      </div>
    </div>

    <!-- Botón genérico sin ruta ni hijos -->
    <button
      v-else
      @click="handleClick()"
      class="sidebar-option sidebar-option-idle group flex items-center gap-3 w-full p-2.5 rounded-lg border border-transparent transition-all duration-200 ease-in-out"
      :class="[{ 'justify-center': !isExpanded }]"
    >
      <component
        :is="icon"
        :size="20"
        :stroke-width="2"
        class="shrink-0 text-[#565b9a] transition-colors duration-200 group-hover:text-[#7c4ff3] dark:text-white/75 dark:group-hover:text-white"
      />
      <span
        v-if="isExpanded"
        class="text-sm font-medium text-[#565b9a] transition-colors duration-200 truncate group-hover:text-[#7c4ff3] dark:text-white/75 dark:group-hover:text-white"
      >
        {{ label }}
      </span>
    </button>
  </li>
</template>

<style scoped>
.sidebar-option {
  backdrop-filter: blur(14px) saturate(145%);
  -webkit-backdrop-filter: blur(14px) saturate(145%);
}

.sidebar-option-idle:hover {
  background: linear-gradient(135deg, rgb(169 126 255 / 0.1) 0%, rgb(199 176 255 / 0.16) 100%);
  box-shadow:
    0 6px 16px rgb(126 87 194 / 0.08),
    inset 0 1px 0 rgb(255 255 255 / 0.42);
}

.sidebar-option-active {
  border-color: rgb(255 255 255 / 0.28);
  background: linear-gradient(195deg, #b783ffd0 0%, #9b63f5de 48%, #7e49e8fa 100%);
  box-shadow:
    0 2px 10px rgba(123, 69, 232, 0.247),
    inset 0 1px 0 rgba(248, 248, 248, 0.192),
    inset 0 0px 0 rgba(229, 218, 255, 0.219);
}

.sidebar-option-idle-dark:hover {
  border-color: rgb(255 255 255 / 0.08);
  background: linear-gradient(135deg, rgb(139 92 246 / 0.2) 0%, rgb(196 181 253 / 0.1) 100%);
  box-shadow:
    0 8px 18px rgb(0 0 0 / 0.18),
    inset 0 1px 0 rgb(255 255 255 / 0.1);
}

.sidebar-option-active-dark {
  border-color: rgb(255 255 255 / 0.12);
  background: linear-gradient(135deg, rgb(138 92 246 / 0.096) 0%, rgb(195 181 253 / 0.103) 100%);
  box-shadow:
    0 10px 24px rgb(0 0 0 / 0.2),
    inset 0 1px 0 rgb(255 255 255 / 0.16),
    inset 0 -1px 0 rgb(255 255 255 / 0.06);
}
</style>
